import React, { useEffect, useState, useCallback } from 'react';
import { Search, Package, X, Loader2, Clock, Truck, CheckCircle2, XCircle, Eye, AlertTriangle, Phone, Mail } from 'lucide-react';
import { AdminAPI, formatPrice } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';

const STATUS_FILTERS = [
  { id: 'all', label: 'Toutes', cls: 'text-[#7A6F5C]' },
  { id: 'pending', label: 'En attente', cls: 'text-amber-700' },
  { id: 'preparing', label: 'Préparation', cls: 'text-blue-700' },
  { id: 'shipped', label: 'Expédiées', cls: 'text-[#1F4D3F]' },
  { id: 'delivered', label: 'Livrées', cls: 'text-emerald-700' },
  { id: 'cancelled', label: 'Annulées', cls: 'text-rose-700' },
];

const STATUS_META = {
  pending: { label: 'En attente', icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  preparing: { label: 'Préparation', icon: Package, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  shipped: { label: 'Expédiée', icon: Truck, cls: 'bg-[#E4EDE9] text-[#1F4D3F] border-[#1F4D3F]/30' },
  delivered: { label: 'Livrée', icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Annulée', icon: XCircle, cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const OrdersAdminTab = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orderList, st] = await Promise.all([
        AdminAPI.orders({ status: filter, q }),
        AdminAPI.orderStats().catch(() => null),
      ]);
      setOrders(Array.isArray(orderList) ? orderList : []);
      if (st) setStats(st);
    } finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [filter, load]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await AdminAPI.cancelOrder(cancelTarget.id, cancelReason);
      toast({ title: '✅ Commande annulée', description: 'Le client a été notifié par email.' });
      setCancelTarget(null);
      setCancelReason('');
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Annulation impossible' });
    } finally { setCancelling(false); }
  };

  return (
    <div data-testid="admin-orders-tab">
      {/* Stats banner */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <div className="bg-white border border-[#E8E0D0] rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#7A6F5C]">Total commandes</div>
            <div className="font-serif text-2xl text-[#1F4D3F] font-semibold mt-0.5">{stats.total_orders}</div>
          </div>
          <div className="bg-white border border-[#E8E0D0] rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#7A6F5C]">Revenu total</div>
            <div className="font-serif text-2xl text-[#1F4D3F] font-semibold mt-0.5">{formatPrice(stats.total_revenue)}</div>
          </div>
          {['pending', 'shipped', 'delivered'].map((s) => {
            const data = stats.by_status?.[s];
            const meta = STATUS_META[s];
            return (
              <div key={s} className="bg-white border border-[#E8E0D0] rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#7A6F5C] flex items-center gap-1">
                  <meta.icon className="w-3 h-3" /> {meta.label}
                </div>
                <div className="font-serif text-2xl text-[#1F4D3F] font-semibold mt-0.5">{data?.count || 0}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A6F5C]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par ID, email, nom ou téléphone…" className="pl-9" data-testid="admin-orders-search" />
        </div>
        <div className="flex gap-1 rounded-lg bg-[#FAF7F2] border border-[#E8E0D0] p-0.5 overflow-x-auto">
          {STATUS_FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`admin-orders-filter-${f.id}`}
              className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${filter === f.id ? 'bg-[#1F4D3F] text-white' : `${f.cls} hover:text-[#1F4D3F]`}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Package className="w-10 h-10 mx-auto mb-3 opacity-50" />Aucune commande pour ce filtre.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Commande</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const meta = STATUS_META[o.status] || STATUS_META.pending;
                const Icon = meta.icon;
                const canCancel = !['delivered', 'cancelled'].includes(o.status);
                return (
                  <tr key={o.id} className="border-t border-[#F5F1EA]" data-testid={`admin-order-row-${o.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-[#2D2A26]">{o.id}</div>
                      <div className="text-[10px] text-[#7A6F5C] mt-0.5">{(o.items?.length || 0)} article(s)</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#2D2A26]">{o.buyer_name || '—'}</div>
                      <div className="text-xs text-[#7A6F5C]">{o.buyer_email || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#7A6F5C]">{formatDate(o.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-[#1F4D3F]">{formatPrice(o.total)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${meta.cls}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => setSelectedOrder(o)}
                        className="border-[#E8E0D0] text-[#1F4D3F] mr-1" data-testid={`admin-order-view-${o.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Détails
                      </Button>
                      {canCancel && (
                        <Button size="sm" variant="outline" onClick={() => setCancelTarget(o)}
                          className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid={`admin-order-cancel-${o.id}`}>
                          <X className="w-3.5 h-3.5 mr-1" /> Annuler
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(o) => !o && setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Commande {selectedOrder?.id}</DialogTitle>
            <DialogDescription>Détails complets de la commande et de ses articles</DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#FAF7F2] rounded-lg p-3">
                  <div className="text-xs uppercase tracking-wider text-[#7A6F5C] mb-1">Client</div>
                  <div className="font-medium text-[#2D2A26]">{selectedOrder.buyer_name || '—'}</div>
                  <div className="text-xs flex items-center gap-1 mt-1 text-[#7A6F5C]"><Mail className="w-3 h-3" /> {selectedOrder.buyer_email}</div>
                  {selectedOrder.buyer_phone && <div className="text-xs flex items-center gap-1 mt-0.5 text-[#7A6F5C]"><Phone className="w-3 h-3" /> {selectedOrder.buyer_phone}</div>}
                </div>
                <div className="bg-[#FAF7F2] rounded-lg p-3">
                  <div className="text-xs uppercase tracking-wider text-[#7A6F5C] mb-1">Livraison</div>
                  <div className="text-[#2D2A26]">{selectedOrder.shipping_address?.full_name || '—'}</div>
                  <div className="text-xs text-[#7A6F5C]">{selectedOrder.shipping_address?.address}</div>
                  <div className="text-xs text-[#7A6F5C]">{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.wilaya}</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-[#7A6F5C] mb-2">Articles</div>
                <table className="w-full text-xs">
                  <tbody>
                    {(selectedOrder.items || []).map((it, i) => (
                      <tr key={i} className="border-b border-[#F5F1EA]">
                        <td className="py-2">
                          <div className="font-medium text-[#2D2A26]">{it.name}</div>
                          <div className="text-[#7A6F5C]">{it.variant_label} × {it.qty}</div>
                          {it.supplier_name && (
                            <div className={`text-[10px] mt-0.5 ${it.supplier_orphan ? 'text-rose-700 font-medium' : 'text-emerald-700'}`}>
                              📦 {it.supplier_name} {it.supplier_id ? `· ${it.supplier_id}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="py-2 text-right text-[#2D2A26]">{formatPrice((it.price || 0) * (it.qty || 1))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-[#FAF7F2] rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-[#7A6F5C]"><span>Sous-total</span><span>{formatPrice(selectedOrder.subtotal || selectedOrder.total)}</span></div>
                {selectedOrder.shipping_fee != null && (
                  <div className="flex justify-between text-[#7A6F5C]"><span>Livraison</span><span>{formatPrice(selectedOrder.shipping_fee)}</span></div>
                )}
                <div className="flex justify-between font-semibold text-[#1F4D3F] pt-2 border-t border-[#E8E0D0]"><span>Total</span><span>{formatPrice(selectedOrder.total)}</span></div>
              </div>

              {selectedOrder.cancel_reason && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-rose-800 text-xs">
                  <strong>Motif d'annulation :</strong> {selectedOrder.cancel_reason}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-600" /> Annuler la commande</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Un email d'annulation sera envoyé au client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#FAF7F2] rounded-lg p-3 text-sm">
              <div className="font-mono text-xs">{cancelTarget?.id}</div>
              <div className="text-[#7A6F5C] text-xs mt-1">{cancelTarget?.buyer_email} · {formatPrice(cancelTarget?.total || 0)}</div>
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Motif (optionnel)</label>
              <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Ex : produit en rupture chez le fournisseur" data-testid="admin-cancel-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Retour</Button>
            <Button onClick={doCancel} disabled={cancelling} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="admin-cancel-confirm">
              {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersAdminTab;
